import { Workspace, WorkspaceVisibility } from "../../types/workspace";
import { axiosInstance } from "../../utils/axios";
import {
  softwareProductCategory,
  totalExternalItSpendCategory,
} from "../../utils/consts";
import { BACCHUS_API_URL, DEFAULT_SAM, DEFAULT_SOM } from "./constants";

const apiInst = axiosInstance;

export const getWorkspace = async (id: string) => {
  const workspace = await apiInst.get<{ data: Workspace }>(
    `${BACCHUS_API_URL}/workspaces/${id}`
  );
  return workspace.data.data;
};

export async function getMyWorkspaces() {
  try {
    const workspace = await apiInst.get<{ data: Workspace[] }>(
      `${BACCHUS_API_URL}/workspaces?filter_by=my`
    );
    return { data: workspace.data.data, error: null };
  } catch (error) {
    console.error("Failed to get my workspaces", error);
    return { error: { message: "Failed to get my workspaces" } };
  }
}

export async function getPublicWorkspaces() {
  try {
    const workspace = await apiInst.get<{ data: Workspace[] }>(
      `${BACCHUS_API_URL}/workspaces?filter_by=org`
    );
    return { data: workspace.data.data, error: null };
  } catch (error) {
    console.error("Failed to get public workspaces", error);
    return { error: { message: "Failed to get public workspaces" } };
  }
}

export type WorkspaceFilterBy = "my" | "org" | "all";

export async function getWorkspaces(filterBy: WorkspaceFilterBy = "my") {
  try {
    const workspace = await apiInst.get<{ data: Workspace[] }>(
      `${BACCHUS_API_URL}/workspaces?filter_by=${filterBy}`
    );
    return { data: workspace.data.data, error: null };
  } catch (error) {
    console.error(`Failed to get workspaces (filter: ${filterBy})`, error);
    return { error: { message: "Failed to get workspaces" } };
  }
}

//TODO: remove additionalFilters from being omitted once backend can receive that property
export const createWorkspace = (workspace: Partial<Workspace>) => {
  return apiInst.post<{ data: Workspace }>(
    `${BACCHUS_API_URL}/workspaces`,
    workspace
  );
};

export function buildNewWorkspace(
  name: string,
  visibility: WorkspaceVisibility,
  description: string,
  itSpendCategory?: string,
  productCategory?: string
): Partial<Workspace> {
  return {
    name,
    configuration: {
      tam: {
        spendCategoryIds: [itSpendCategory || totalExternalItSpendCategory],
        spendCategoryWeights: [
          { id: itSpendCategory || totalExternalItSpendCategory, weight: 100 },
        ],
      },
      sam: DEFAULT_SAM,
      som: DEFAULT_SOM,
      productCategoryIds: productCategory
        ? [productCategory]
        : [...softwareProductCategory],
      competitorsProductIds: [],
      excludedCompetitorsProductIds: [],
      whitespace: null,
      icp: null,
      localizeInstalls: true,
    },
    userFilters: undefined,
    description,
    visibility,
  };
}

export const createDefaultWorkspace = async (
  itSpendCategory: string,
  productCategory: string | undefined
) => {
  const defaultWorkspace = buildNewWorkspace(
    "Default Workspace",
    WorkspaceVisibility.PRIVATE,
    "Default Workspace Description",
    itSpendCategory,
    productCategory
  );

  const res = await createWorkspace(defaultWorkspace);
  return res.data.data;
};

export const duplicateWorkspace = async (id: string) => {
  try {
    await apiInst.post<{ data: Workspace }>(
      `${BACCHUS_API_URL}/workspaces/${id}/duplicate`
    );
    return { error: null };
  } catch (error) {
    console.error("Failed duplicate workspace", error);
    return { data: null, error: { message: "Failed duplicate workspace" } };
  }
};

export const deleteWorkspace = async (id: string) => {
  try {
    await apiInst.delete<{ data: Workspace }>(
      `${BACCHUS_API_URL}/workspaces/${id}`
    );
    return { error: null };
  } catch (error) {
    console.error("Failed to delete workspace", error);
    return { error: { message: "Failed to delete workspace" } };
  }
};

const updateWorkspaceVisibility = async (id: string, visibility: string) => {
  try {
    await apiInst.patch<{ data: Workspace }>(
      `${BACCHUS_API_URL}/workspaces/${id}`,
      { visibility }
    );
    return { error: null };
  } catch (error) {
    console.error("Failed to set workspace to " + visibility, error);
    return { error: { message: "Failed to set workspace to " + visibility } };
  }
};

export const setWorkspaceToPublic = async (id: string) =>
  await updateWorkspaceVisibility(id, WorkspaceVisibility.PUBLIC);

export const setWorkspaceToPrivate = async (id: string) =>
  await updateWorkspaceVisibility(id, WorkspaceVisibility.PRIVATE);

export async function updateWorkspace(workspace: {
  id: string;
  name: string;
  visibility: WorkspaceVisibility;
  description: string;
}) {
  try {
    await apiInst.patch<{ data: Workspace }>(
      `${BACCHUS_API_URL}/workspaces/${workspace.id}`,
      {
        name: workspace.name,
        visibility: workspace.visibility,
        description: workspace.description,
      }
    );
    return { isSuccessful: true };
  } catch (error) {
    console.error("Failed to update workspace", error);
    return { isSuccessful: false };
  }
}

export const saveWorkspace = async (
  id: string,
  workspaceData: Partial<Workspace>
) => {
  try {
    const response = await apiInst.patch<{ data: Workspace }>(
      `${BACCHUS_API_URL}/workspaces/${id}`,
      workspaceData
    );
    return { data: response.data.data, error: null };
  } catch (error) {
    console.error("Failed to save workspace", error);
    return { data: null, error: { message: "Failed to save workspace" } };
  }
};

export const saveAsNewWorkspace = async (
  id: string,
  params: Partial<Workspace>
) => {
  try {
    const response = await apiInst.post<{ data: Workspace }>(
      `${BACCHUS_API_URL}/workspaces/${id}/save_as_new`,
      params
    );
    return { data: response.data.data, error: null };
  } catch (error) {
    console.error("Failed to save as new workspace", error);
    return {
      data: null,
      error: { message: "Failed to save as new workspace" },
    };
  }
};

export const renameWorkspace = async (id: string, name: string) => {
  try {
    const response = await apiInst.patch<{ data: Workspace }>(
      `${BACCHUS_API_URL}/workspaces/${id}`,
      { name }
    );
    return { data: response.data.data, error: null };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    // Handle specific name validation errors (422 status)
    if (error.response && error.response.status === 422) {
      const nameErrors = error.response.data?.errors?.name || [];

      if (nameErrors.length > 0) {
        return {
          data: null,
          error: {
            message: `Workspace name ${nameErrors[0]}`,
            code: 422,
          },
        };
      }

      // Fallback for any other validation errors
      return {
        data: null,
        error: {
          message: "Invalid workspace name",
          code: 422,
        },
      };
    }

    console.error("Failed to rename workspace", error);
    return { data: null, error: { message: "Failed to rename workspace" } };
  }
};
